import React, { useEffect, useRef, useState } from 'react';
import {
  Button, Card, ConfigProvider, message,
  Popover, Spin, Table, Typography,
} from 'antd';
import { EyeOutlined, SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import App from '../LayoutMobile';
import { Config } from '../Common/Config';
import './Patients.css';

const { Title } = Typography;

// ── Helpers ────────────────────────────────────────────────────────────────
const getInitials = (name = '', surname = '') => {
  const a = (name || '').trim()[0] || '';
  const b = (surname || '').trim()[0] || '';
  return (a + b).toUpperCase() || 'P';
};

const formatDateTime = (isoString) => {
  if (!isoString) return '-';
  try {
    const d = new Date(isoString);
    return d.toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch (_) {
    return isoString;
  }
};

// Strip duplicate country-code digits from phone_number then format
// e.g. country_code="+44" + phone_number="447350099554" → "+44 7350099554"
const formatPhone = (countryCode, phoneNumber) => {
  if (!phoneNumber) return null;
  const cc = (countryCode || '').replace('+', ''); // "44"
  // Remove leading + if present, then strip cc digits from the front
  let num = phoneNumber.startsWith('+') ? phoneNumber.slice(1) : phoneNumber;
  if (cc && num.startsWith(cc)) num = num.slice(cc.length);
  return countryCode ? `${countryCode} ${num}` : num;
};

// Convert YYYY-MM-DD → DD/MM/YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const [y, m, d] = dateStr.split('-');
  if (!y || !m || !d) return dateStr;
  return `${d}/${m}/${y}`;
};

// Format postal code — UK: insert space before last 3 chars, uppercase
// e.g. "as23df" → "AS2 3DF" | "SW1A1AA" → "SW1A 1AA" | "SS99TT" → "SS9 9TT"
// Already-spaced codes and non-UK lengths are just uppercased
const formatPostalCode = (code) => {
  if (!code) return '-';
  const cleaned = code.replace(/\s+/g, '').toUpperCase();
  if (cleaned.length >= 5 && cleaned.length <= 7) {
    return `${cleaned.slice(0, -3)} ${cleaned.slice(-3)}`;
  }
  return cleaned;
};

// ── Component ──────────────────────────────────────────────────────────────
const Patients = () => {
  const [patientList, setPatientList]         = useState([]);
  const [pagination, setPagination]           = useState({});
  const [currentPage, setCurrentPage]         = useState(1);
  const [perPage, setPerPage]                 = useState(20);
  const [searchKeyword, setSearchKeyword]     = useState('');
  const [loader, setLoader]                   = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [viewLoader, setViewLoader]           = useState(false);
  const [isViewOpen, setIsViewOpen]           = useState(false);
  const [messageApi, contextHolder]           = message.useMessage();

  const searchTimerRef = useRef(null);
  const hasFetched     = useRef(false);

  // ── Initial fetch ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!hasFetched.current) {
      fetchPatients(1, '');
      hasFetched.current = true;
    }
  }, []);

  // ── API: list patients ───────────────────────────────────────────────────
  const fetchPatients = async (page = 1, search = '', limit = perPage) => {
    setLoader(true);
    try {
      const params = {};
      if (search && search.trim()) params.search = search.trim();
      params.per_page = limit;

      const response = await axios.get(
        `${Config.urlLogin}/api/admin/v1/patients/page/${page}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          params,
        }
      );

      if (response?.data?.success && response.data.data) {
        const meta = response.data.data;
        setPatientList(meta.data || []);
        setPagination(meta);
      } else {
        setPatientList([]);
        setPagination({});
      }
    } catch (error) {
      messageApi.error(
        error.response?.data?.message || error.message || 'Something went wrong'
      );
      setPatientList([]);
      setPagination({});
    } finally {
      setLoader(false);
    }
  };

  // ── API: single patient detail ───────────────────────────────────────────
  const fetchPatientDetail = async (patientId) => {
    setViewLoader(true);
    setIsViewOpen(true);
    setSelectedPatient(null);
    try {
      const response = await axios.get(
        `${Config.urlLogin}/api/admin/v1/patients/${patientId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      if (response?.data?.success && response.data.data) {
        setSelectedPatient(response.data.data);
        // Scroll to detail panel
        setTimeout(() => {
          document.querySelector('.patient-detail-panel')?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        messageApi.error('Patient not found.');
        setIsViewOpen(false);
      }
    } catch (error) {
      messageApi.error(error.response?.data?.message || 'Patient not found.');
      setIsViewOpen(false);
    } finally {
      setViewLoader(false);
    }
  };

  // ── Close detail panel ───────────────────────────────────────────────────
  const handleCloseView = () => {
    setIsViewOpen(false);
    setSelectedPatient(null);
  };

  // ── Table columns ────────────────────────────────────────────────────────
  const columns = [
    { title: 'First Name', dataIndex: 'name',    key: 'name'    },
    { title: 'Last Name',  dataIndex: 'surname', key: 'surname' },
    { title: 'Email',      dataIndex: 'email',   key: 'email'   },
    {
      title: '',
      key: 'action',
      render: (_, record) => (
        <div style={{ textAlign: 'right', marginRight: 2 }} onClick={(e) => e.stopPropagation()}>
          <Popover placement="left" trigger="click" content={() => popoverContent(record)}>
            <button
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontWeight: 700, fontSize: 18, letterSpacing: 2,
                color: '#008385', padding: '0 6px', lineHeight: 1,
              }}
            >
              ···
            </button>
          </Popover>
        </div>
      ),
    },
  ];

  // ── Popover (view only) ──────────────────────────────────────────────────
  const popoverContent = (record) => (
    <div className="rule-action-popover" style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
      <div
        className="rule-action-item"
        style={{ color: '#008385' }}
        onClick={(e) => { e.stopPropagation(); fetchPatientDetail(record.patient_id); }}
      >
        <EyeOutlined style={{ fontSize: 15 }} /> View
      </div>
    </div>
  );


  // ── Detail view rows helper ──────────────────────────────────────────────
  const DetailRow = ({ label, value, mono = false, hide = false }) => {
    if (hide) return null;
    return (
      <div className="clinician-view-detail-row">
        <label>{label}</label>
        <span
          className="clinician-view-field-value"
          style={mono ? { fontFamily: 'Courier New, monospace', fontSize: 13 } : {}}
        >
          {value || '-'}
        </span>
      </div>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <App>
      {contextHolder}

      <div style={{ display: 'grid', justifyContent: 'center' }}>

        {/* ── Header: title + search ──────────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>

          {/* Title */}
          <Title level={5} style={{ margin: 0, color: '#008385', fontSize: 20 }}>
            All Patients{' '}
            <span style={{ color: 'black', fontWeight: '400' }}>
              ({pagination?.total ?? ''})
            </span>
          </Title>

          {/* Search */}
          <div
            style={{
              display: 'flex', alignItems: 'center',
              border: '1px solid #d9d9d9', borderRadius: 6,
              overflow: 'hidden', height: 40, background: '#fff',
              transition: 'border-color 0.2s',
            }}
            onFocusCapture={(e) => { e.currentTarget.style.borderColor = '#008385'; }}
            onBlurCapture={(e)  => { e.currentTarget.style.borderColor = '#d9d9d9'; }}
          >
            <input
              type="text"
              placeholder="Search patients..."
              value={searchKeyword}
              onChange={(e) => {
                const val = e.target.value;
                setSearchKeyword(val);
                if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
                searchTimerRef.current = setTimeout(() => {
                  setCurrentPage(1);
                  fetchPatients(1, val);
                }, 500);
              }}
              style={{
                border: 'none', outline: 'none', padding: '0 12px',
                fontSize: 14, color: '#333', width: 230,
                height: '100%', background: 'transparent',
              }}
            />
            <button
              onClick={() => {
                if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
                setCurrentPage(1);
                fetchPatients(1, searchKeyword);
              }}
              style={{
                background: 'none', border: 'none',
                borderLeft: '1px solid #d9d9d9', cursor: 'pointer',
                padding: '0 12px', height: '100%',
                display: 'flex', alignItems: 'center',
                color: '#008385', fontSize: 16,
              }}
            >
              <SearchOutlined />
            </button>
          </div>
        </div>

        {/* ── Table ───────────────────────────────────────────────────── */}
        <Card
          style={{
            width: 937, margin: 0, padding: 0,
            borderRadius: 40, boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
          }}
        >
          <ConfigProvider
            theme={{
              components: {
                Table: { headerBg: '#008385', headerColor: '#ffffff' },
                Spin:  { colorPrimary: '#008385' },
              },
            }}
          >
            <Table
              className="patients-table"
              loading={loader}
              columns={columns}
              dataSource={patientList}
              rowKey="patient_id"
              pagination={{
                pageSize: perPage,
                total:    pagination?.total || 0,
                current:  currentPage,
                showSizeChanger: true,
                pageSizeOptions: ['20', '50', '100', '200'],
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} patients`,
                onChange: (page) => {
                  setCurrentPage(page);
                  fetchPatients(page, searchKeyword);
                },
                onShowSizeChange: (current, size) => {
                  setPerPage(size);
                  setCurrentPage(1);
                  // Use size directly — perPage state update is async
                  const params = {};
                  if (searchKeyword && searchKeyword.trim()) params.search = searchKeyword.trim();
                  params.per_page = size;
                  setLoader(true);
                  axios.get(
                    `${Config.urlLogin}/api/admin/v1/patients/page/1`,
                    { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` }, params }
                  ).then(res => {
                    if (res?.data?.success && res.data.data) {
                      setPatientList(res.data.data.data || []);
                      setPagination(res.data.data);
                    }
                  }).catch(err => {
                    messageApi.error(err.response?.data?.message || 'Something went wrong');
                  }).finally(() => setLoader(false));
                },
                itemRender: (current, type, originalElement) => {
                  if (type === 'prev') return <a>Previous</a>;
                  if (type === 'next') return <a>Next</a>;
                  return originalElement;
                },
              }}
              onRow={(record) => ({
                onClick: () => fetchPatientDetail(record.patient_id),
                style:   { cursor: 'pointer' },
              })}
            />
          </ConfigProvider>
        </Card>
      </div>

      {/* ── Patient detail view panel ────────────────────────────────── */}
      {isViewOpen && (
        <div className="patient-detail-panel">
          <Card
            className="clinician-view-mode-card"
            style={{ width: 705, margin: 0, marginTop: 20, borderRadius: 8, overflow: 'hidden' }}
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div
                  style={{
                    width: 38, height: 38, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #008385, #00a3a5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontWeight: 700, fontSize: 15,
                    flexShrink: 0, boxShadow: '0 2px 6px rgba(0,131,133,0.30)',
                  }}
                >
                  {getInitials(selectedPatient?.name, selectedPatient?.surname)}
                </div>
                <span style={{ fontWeight: 700, fontSize: 16, color: '#007a7c' }}>
                  {[selectedPatient?.name, selectedPatient?.surname].filter(Boolean).join(' ') || 'Patient'}
                </span>
              </div>
            }
            extra={null}
          >
            {viewLoader ? (
              <ConfigProvider theme={{ components: { Spin: { colorPrimary: '#008385' } } }}>
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                  <Spin size="large" />
                </div>
              </ConfigProvider>
            ) : selectedPatient ? (
              <div style={{ padding: '0 4px' }}>
                <DetailRow label="First Name"     value={selectedPatient.name} />
                <DetailRow label="Last Name"      value={selectedPatient.surname} />
                <DetailRow label="Email"          value={selectedPatient.email} />
                <DetailRow
                  label="Phone"
                  value={formatPhone(selectedPatient.country_code, selectedPatient.phone_number)}
                />
                <DetailRow label="Date of Birth"  value={formatDate(selectedPatient.date_of_birth)} />
                <DetailRow label="Gender"         value={selectedPatient.gender} />
                <DetailRow label="Address Line 1" value={selectedPatient.address_1} />
                <DetailRow
                  label="Address Line 2"
                  value={selectedPatient.address_2}
                  hide={!selectedPatient.address_2}
                />
                <DetailRow label="City"           value={selectedPatient.city} />
                <DetailRow label="Country"        value={selectedPatient.country} />
                <DetailRow label="Postal Code"    value={formatPostalCode(selectedPatient.postal_code)} />
                <DetailRow
                  label="Last Login"
                  value={formatDateTime(selectedPatient.last_login_at)}
                />

                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20, paddingBottom: 4 }}>
                  <Button
                    onClick={handleCloseView}
                    style={{ borderColor: '#008385', color: '#008385', minWidth: 100 }}
                  >
                    Close
                  </Button>
                </div>
              </div>
            ) : null}
          </Card>
        </div>
      )}
    </App>
  );
};

export default Patients;
